import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import { SOCIAL_LINKS } from "@/lib/socials";

const LOGO_URL = "/midemide-mark.png";

// TikTok icon (lucide doesn't include one)
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.07 8.07 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.59z"/>
  </svg>
);
export default function Footer() {
  const { t } = useT();
  const serviceItems = [
    t("svcOpt.ltl.label"),
    t("svcOpt.ftl.label"),
    t("svcOpt.flatbed.label"),
    t("svcOpt.conestoga.label"),
    t("svcOpt.specialized.label"),
    t("svcOpt.container.label"),
    t("svcOpt.cross_border.label"),
    t("svcOpt.international.label"),
    t("svcOpt.small_package.label"),
    t("svcOpt.warehousing.label"),
  ];
  return (
    <footer data-testid="site-footer" className="bg-[#070707] border-t border-white/10 mt-0">
      <div className="container-mdgx pt-20 pb-10">
        {/* Massive brand */}
        <div className="border-b border-white/10 pb-12 mb-12">
          <h2 className="font-display font-black text-white text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] xl:text-[8rem] leading-[0.95] tracking-tighter break-words">
            MIDEMIDE
            <span className="text-[#D9633F]">.</span>
          </h2>
          <p className="text-white/50 mt-3 text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] uppercase">{t("common.streamliningLogistics")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4 mb-5">
              <img src={LOGO_URL} alt="Midemide Logistics" loading="lazy" decoding="async" width="56" height="56" className="h-14 w-14 object-contain" />
              <div className="font-display font-bold text-white">Midemide Logistics Inc.</div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {t("footer.tagline")}
            </p>
            <div className="flex items-center gap-3">
              <a data-testid="footer-social-facebook" href={SOCIAL_LINKS.facebook.url} target="_blank" rel="noopener noreferrer" aria-label={`${SOCIAL_LINKS.facebook.label} — ${SOCIAL_LINKS.facebook.handle}`} className="w-10 h-10 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/80 flex items-center justify-center transition-colors">
                <Facebook size={16} />
              </a>
              <a data-testid="footer-social-instagram" href={SOCIAL_LINKS.instagram.url} target="_blank" rel="noopener noreferrer" aria-label={`${SOCIAL_LINKS.instagram.label} — ${SOCIAL_LINKS.instagram.handle}`} className="w-10 h-10 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/80 flex items-center justify-center transition-colors">
                <Instagram size={16} />
              </a>
              <a data-testid="footer-social-linkedin" href={SOCIAL_LINKS.linkedin.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL_LINKS.linkedin.label} className="w-10 h-10 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/80 flex items-center justify-center transition-colors">
                <Linkedin size={16} />
              </a>
              <a data-testid="footer-social-tiktok" href={SOCIAL_LINKS.tiktok.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL_LINKS.tiktok.label} className="w-10 h-10 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/80 flex items-center justify-center transition-colors">
                <TikTokIcon size={16} />
              </a>
              <a data-testid="footer-social-youtube" href="#" aria-label="YouTube" className="w-10 h-10 border border-white/15 hover:border-[#D9633F] hover:text-[#D9633F] text-white/80 flex items-center justify-center transition-colors">
                <Youtube size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-overline mb-4">{t("footer.services")}</div>
            <ul className="space-y-2 text-sm">
              {serviceItems.map((s) => (
                <li key={s}>
                  <Link to="/services" className="text-white/70 hover:text-[#D9633F] transition-colors">{s}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-overline mb-4">{t("footer.company")}</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-white/70 hover:text-[#D9633F]">{t("nav.about")}</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-[#D9633F]">{t("nav.blog")}</Link></li>
              <li><Link to="/quote" className="text-white/70 hover:text-[#D9633F]">{t("nav.requestQuote")}</Link></li>
              <li><Link to="/track" className="text-white/70 hover:text-[#D9633F]">{t("nav.track")}</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-[#D9633F]">{t("nav.contact")}</Link></li>
              <li><Link to="/login" className="text-white/70 hover:text-[#D9633F]">{t("nav.login")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-overline mb-4">{t("footer.contact")}</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 text-white/80">
                <Phone size={15} className="text-[#D9633F] mt-0.5 shrink-0" />
                <a href="tel:+19053637673" className="hover:text-[#D9633F]">+1 (905) 363-7673</a>
              </li>
              <li className="flex items-start gap-3 text-white/80">
                <Phone size={15} className="text-[#D9633F] mt-0.5 shrink-0" />
                <a href="tel:+12893011919" className="hover:text-[#D9633F]">+1 (289) 301-1919</a>
              </li>
              <li className="flex items-start gap-3 text-white/80">
                <Mail size={15} className="text-[#D9633F] mt-0.5 shrink-0" />
                <a href="mailto:dispatch@midemidelogistics.com" className="hover:text-[#D9633F] break-all">dispatch@midemidelogistics.com</a>
              </li>
              <li className="flex items-start gap-3 text-white/80">
                <Mail size={15} className="text-[#D9633F] mt-0.5 shrink-0" />
                <a href="mailto:info@midemidelogistics.com" className="hover:text-[#D9633F] break-all">info@midemidelogistics.com</a>
              </li>
              <li className="flex items-start gap-3 text-white/70">
                <MapPin size={15} className="text-[#D9633F] mt-0.5 shrink-0" />
                <span>B22/23 - 1266 South Service Road<br/>Stoney Creek, Ontario L8E-5R9<br/>Canada</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col md:flex-row justify-between gap-4 items-center">
          <div className="text-white/40 text-xs tracking-wide">
            © {new Date().getFullYear()} Midemide Logistics Inc. {t("footer.allRights")}
          </div>
          <div className="text-white/40 text-xs font-mono tracking-wider">MDLGX · BROKER · CANADA · USA · INTL</div>
        </div>
      </div>
    </footer>
  );
}
